import { ClockIcon, FileIcon } from "lucide-react";

import { formatearMomento } from "@/lib/fechas";
import type { FacturacionDelInforme } from "@/lib/informes";

/**
 * Los datos con los que se le va a facturar a este cliente, tal como los cargó
 * en su portal.
 *
 * ══ POR QUÉ ES DE SOLO LECTURA ══════════════════════════════════════════════
 *
 * Porque el dato es del CLIENTE y su única puerta hoy es el portal. Un
 * formulario editable acá abriría un segundo camino de escritura sin que nadie
 * lo haya pedido, y con él la pregunta de cuál manda cuando difieren. El día
 * que haga falta —el ejecutivo transcribiendo lo que le dictan por teléfono— es
 * un endpoint interno y `origen` ya distingue las dos procedencias.
 *
 * ══ POR QUÉ VIVE EN ANTECEDENTES GENERALES ══════════════════════════════════
 *
 * Porque describe al CLIENTE, y esa pestaña es la ficha del cliente y de la
 * propiedad: quien completa ahí su nombre, su contacto y su dirección tiene al
 * lado con qué datos se le va a facturar. Estuvo en Antecedentes legales, junto
 * a los documentos que el cliente sube por ese mismo portal, y se movió porque
 * la pregunta que responde es "quién es este cliente", no "qué papeles faltan".
 *
 * Y vive en UN solo lugar: la misma tarjeta repetida en dos pestañas es el
 * mismo markup mantenido dos veces para un dato que no cambia entre ellas.
 *
 * ══ EL VACÍO NO ES UN HUECO ═════════════════════════════════════════════════
 *
 * "Todavía no los mandó" es un estado del proceso, no una tarjeta rota, y viene
 * acompañado de si el cliente llegó a ABRIR el portal — que es lo que decide
 * entre esperar y levantar el teléfono. Un cliente que entró y no completó
 * necesita una llamada distinta de uno que nunca vio el correo.
 */
export function FacturacionDelCliente({
  facturacion,
  visitadoEn,
}: {
  facturacion: FacturacionDelInforme | null | undefined;
  /** Cuándo abrió el cliente su portal, o `null` si nunca entró. */
  visitadoEn: string | null | undefined;
}) {
  const direccion =
    [facturacion?.direccion, facturacion?.comuna].filter(Boolean).join(", ") || null;
  const contacto =
    [facturacion?.contacto_nombre, facturacion?.telefono].filter(Boolean).join(" · ") || null;

  return (
    <section className="border-border bg-superficie-sutil rounded-xl border-[1.5px] px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <FileIcon className="text-ink-hint size-4 shrink-0" aria-hidden="true" />
        <h3 className="text-ink text-label">Datos de facturación</h3>
        <span className="bg-muted text-ink-muted flex-none rounded-sm px-2 py-0.5 text-[0.625rem] font-extrabold tracking-[0.05em] uppercase">
          Solo lectura
        </span>

        <span className="text-ink-hint ml-auto text-[0.71875rem] font-semibold">
          <span>Los declara el cliente en su portal</span>
          {facturacion?.actualizado_en ? (
            <>
              {" · "}
              <span className="font-mono">{formatearMomento(facturacion.actualizado_en)}</span>
            </>
          ) : null}
        </span>
      </div>

      {/* Los cuatro datos de siempre van SIEMPRE, con «—» cuando el cliente no
          los mandó: la grilla no cambia de forma según haya o no datos. */}
      <dl className="mt-3 grid gap-x-[1.125rem] gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Dato etiqueta="RUT" valor={facturacion?.rut ?? null} mono />
        <Dato etiqueta="Razón social" valor={facturacion?.razon_social ?? null} />
        <Dato etiqueta="Giro" valor={facturacion?.giro ?? null} />
        <Dato etiqueta="Correo de facturación" valor={facturacion?.email_facturacion ?? null} />
      </dl>

      {/* Dirección y contacto no están en el diseño, pero son datos que el
          cliente sí declara: solo se dibujan si tienen valor. */}
      {direccion || contacto ? (
        <dl className="mt-3 grid gap-x-[1.125rem] gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {direccion ? <Dato etiqueta="Dirección" valor={direccion} /> : null}
          {contacto ? <Dato etiqueta="Contacto" valor={contacto} /> : null}
        </dl>
      ) : null}

      {!facturacion ? (
        <div className="mt-3 space-y-1">
          <p className="text-ink-muted text-caption">
            El cliente todavía no los envió. Se los pedimos en el correo de aceptación, con un
            enlace a su portal.
          </p>

          <p className="text-ink-hint flex items-center gap-1.5 text-xs">
            <ClockIcon className="size-3.5 shrink-0" aria-hidden="true" />
            {visitadoEn
              ? `Abrió el portal el ${formatearMomento(visitadoEn)}.`
              : "Todavía no abrió el portal."}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function Dato({
  etiqueta,
  valor,
  mono = false,
}: {
  etiqueta: string;
  valor: string | null;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-ink-hint text-[0.625rem] font-extrabold tracking-[0.05em] uppercase">{etiqueta}</dt>
      {/* Un guion y no un vacío: una celda en blanco se lee como un error de
          carga, y "—" dice que el dato no vino. Mismo criterio que el documento
          de cotización usa con las superficies. */}
      <dd className={mono ? "text-ink font-mono text-sm" : "text-ink text-sm"}>{valor ?? "—"}</dd>
    </div>
  );
}
